import { useCallback, useEffect, useState } from "react";
import { logger } from "@/logger";
import { useWebSocketClient, useWebSocketConnection, useWebSocketEvent } from "./hooks";
import type { TypingEventDto } from "./types";

interface TypingUser {
  userId: string;
  displayName: string;
  timestamp: number;
}

/**
 * Hook for managing conversation room membership Automatically joins when mounted and leaves when
 * unmounted
 */
export function useConversationRoom(conversationId: string | undefined) {
  const client = useWebSocketClient();
  const { isConnected } = useWebSocketConnection();

  useEffect(() => {
    if (!conversationId || !client || !isConnected) {
      return;
    }

    client.conversationJoin(conversationId);
    logger.info(`[WebSocket] Joined conversation room: ${conversationId}`);

    return () => {
      client.conversationLeave(conversationId);
      logger.info(`[WebSocket] Left conversation room: ${conversationId}`);
    };
  }, [conversationId, client, isConnected]);
}

/** Hook for typing indicators in a conversation */
export function useTypingIndicator(
  conversationId: string | undefined,
  currentUserId: string | undefined,
  currentUserDisplayName: string | undefined,
) {
  const client = useWebSocketClient();
  const { isConnected } = useWebSocketConnection();
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingUser>>(new Map());

  // Listen for typing start events
  useWebSocketEvent(
    "typing:start",
    (data: TypingEventDto) => {
      if (data.conversationId !== conversationId) {
        return;
      }
      // Don't show our own typing indicator
      if (data.userId === currentUserId) {
        return;
      }

      setTypingUsers((prev) => {
        const next = new Map(prev);
        next.set(data.userId, {
          userId: data.userId,
          displayName: data.displayName,
          timestamp: Date.now(),
        });
        return next;
      });

      // Auto-remove after 5 seconds (in case stop event is missed)
      setTimeout(() => {
        setTypingUsers((prev) => {
          const next = new Map(prev);
          const user = next.get(data.userId);
          // Only remove if timestamp matches (hasn't been updated)
          if (user && Date.now() - user.timestamp >= 4900) {
            next.delete(data.userId);
          }
          return next;
        });
      }, 5000);
    },
    [conversationId, currentUserId],
  );

  // Listen for typing stop events
  useWebSocketEvent(
    "typing:stop",
    (data: TypingEventDto) => {
      if (data.conversationId !== conversationId) {
        return;
      }

      setTypingUsers((prev) => {
        const next = new Map(prev);
        next.delete(data.userId);
        return next;
      });
    },
    [conversationId],
  );

  // Clear typing users when leaving conversation
  useEffect(() => {
    return () => {
      setTypingUsers(new Map());
    };
  }, [conversationId]);

  const startTyping = useCallback(() => {
    if (!client || !isConnected || !conversationId || !currentUserId || !currentUserDisplayName) {
      return;
    }
    client.startTyping(conversationId, currentUserId, currentUserDisplayName);
  }, [client, isConnected, conversationId, currentUserId, currentUserDisplayName]);

  const stopTyping = useCallback(() => {
    if (!client || !isConnected || !conversationId || !currentUserId || !currentUserDisplayName) {
      return;
    }
    client.stopTyping(conversationId, currentUserId, currentUserDisplayName);
  }, [client, isConnected, conversationId, currentUserId, currentUserDisplayName]);

  // Get list of typing display names (excluding current user)
  const typingDisplayNames = Array.from(typingUsers.values()).map((u) => u.displayName);

  return {
    typingUsers,
    typingDisplayNames,
    isTyping: typingDisplayNames.length > 0,
    startTyping,
    stopTyping,
  };
}

/** Hook for presence management */
export function usePresence() {
  const client = useWebSocketClient();
  const { isConnected } = useWebSocketConnection();
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());

  useWebSocketEvent("presence:update", (data) => {
    setOnlineUsers((prev) => {
      const next = new Set(prev);
      if (data.status === "online") {
        next.add(data.userId);
      } else {
        next.delete(data.userId);
      }
      return next;
    });
  });

  const setPresenceOnline = useCallback(() => {
    if (client && isConnected) {
      client.setPresenceOnline();
    }
  }, [client, isConnected]);

  const setPresenceAway = useCallback(() => {
    if (client && isConnected) {
      client.setPresenceAway();
    }
  }, [client, isConnected]);

  const isUserOnline = useCallback((userId: string) => onlineUsers.has(userId), [onlineUsers]);

  return {
    setPresenceOnline,
    setPresenceAway,
    isUserOnline,
    onlineCount: onlineUsers.size,
    onlineUsers,
  };
}
